import * as React from 'react';
import { Check, Download } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import { WorkspaceButton, WorkspaceInput } from '@/components/ui/workspace';
import { formatDay } from '@/components/discovery/discovery-logic';
import { usePacketWait } from '@/components/discovery/signoff/use-packet-wait';
import {
  getSignoffPacket,
  requestSignoffChanges,
  signRequirements,
  type SignoffPayload,
} from '@/lib/api/secure-links';
import { cn } from '@/lib/utils/cn';
import { saveBlob } from '@/lib/utils/download';
import { ClientCard } from './client-frame';
import { nameAndTitle, namesMatch } from './secure-link-logic';
import { DoneCard, problemText, type LinkPageProps } from './secure-link-parts';

/**
 * The signatory's sign-off page (E12-F11 §3; prototype `#client-signoff`).
 * The summary is counted from the frozen snapshot by the server; the
 * packet is the snapshot's PDF. Sign is enabled only with the attestation
 * ticked and a typed name that matches the signatory's person record
 * (Decision 54.38); the server checks both again. "Request changes
 * instead" sends a comment, and sign-off goes back to the consultant.
 *
 * The form shows only while the sign-off is `requested`: a link already
 * answered shows its answer, and any other state says there is nothing to
 * sign (the page re-reads the session when the tab comes back into view).
 * While the packet PDF is still being rendered the page waits for it, then
 * offers it as an "Open packet" button.
 */

const MAX_COMMENT = 4_000;

export function ClientSignoff({
  token,
  session,
  payload,
  onInvalid,
}: LinkPageProps & { payload: SignoffPayload }) {
  const [attested, setAttested] = React.useState(false);
  const [name, setName] = React.useState('');
  const [changes, setChanges] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [problem, setProblem] = React.useState<string | null>(null);
  const [answer, setAnswer] = React.useState<Answer | null>(null);
  // The packet comes with the link header, so it is fetched, then shown in a
  // new tab (or saved, where the browser blocks the tab).
  const showPacket = (blob: Blob) => {
    const url = URL.createObjectURL(blob);
    if (!window.open(url, '_blank')) saveBlob('requirements-packet.pdf', blob);
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };
  const packet = usePacketWait({
    fetchPacket: () => getSignoffPacket(token, payload.packet_url),
    onFile: showPacket,
    onError: onInvalid,
  });

  const signer = payload.signatory;
  const first = session.person_first_name || signer.full_name.split(/\s+/)[0] || '';
  // What was answered here, else what the session says (it may have been
  // answered in another tab since this one loaded).
  const done = answer ?? answerOf(payload);
  if (done?.kind === 'signed') {
    return (
      <DoneCard title="Signed" meta={done.at ? `Signed ${formatDay(done.at)}` : undefined}>
        Thank you{first ? `, ${first}` : ''}. Your sign-off has been received.
      </DoneCard>
    );
  }
  if (done?.kind === 'changes') {
    return (
      <DoneCard title={first ? `Thank you, ${first}` : 'Thank you'}>
        Your request for changes has been received.
      </DoneCard>
    );
  }
  if (payload.state !== 'requested') {
    return (
      <ClientCard>
        <h1 className="mb-1 text-h1 font-semibold text-ink-900">Nothing to sign here</h1>
        <p className="text-body text-ink-700">
          This sign-off request is no longer open. If you expected to sign, reply to the email the
          link came in for a new one.
        </p>
      </ClientCard>
    );
  }

  const nameOk = namesMatch(name, signer.full_name);
  const s = payload.summary;
  const run = async (fn: () => Promise<void>, fallback: string) => {
    setBusy(true);
    setProblem(null);
    try {
      await fn();
    } catch (err) {
      if (!onInvalid(err)) setProblem(problemText(err, fallback));
    } finally {
      setBusy(false);
    }
  };
  const sign = () =>
    run(async () => {
      const res = await signRequirements(token, name);
      setAnswer({ kind: 'signed', at: res.signed_at });
    }, 'Your signature could not be sent.');
  const sendChanges = () =>
    run(async () => {
      await requestSignoffChanges(token, (changes ?? '').trim());
      setAnswer({ kind: 'changes' });
    }, 'Your request could not be sent.');
  const wait = packet.wait;

  return (
    <ClientCard>
      <div className="text-meta text-ink-500">
        {[session.client_name, session.engagement_name].filter(Boolean).join(' · ')} · for{' '}
        {nameAndTitle(signer.full_name, signer.title)}
      </div>
      <h1 className="my-1 text-h1 font-semibold text-ink-900">Requirements sign-off</h1>
      <p className="text-body text-ink-700">
        {payload.requested_by ? `${payload.requested_by}, Avero Advisors,` : 'Avero Advisors'} asks
        you to confirm these requirements before they go to vendors.
      </p>
      <div className="mb-4 mt-5 grid grid-cols-2 gap-3 rounded-card border border-ink-100 bg-paper-50 p-4 sm:grid-cols-4">
        <SumCell
          value={s.requirements}
          label={`${s.requirements === 1 ? 'requirement' : 'requirements'}, ${s.modules} ${s.modules === 1 ? 'module' : 'modules'}`}
        />
        <SumCell value={s.critical} label="critical" />
        <SumCell
          value={s.people_heard}
          label={s.people_heard === 1 ? 'person we heard from' : 'people we heard from'}
        />
        <SumCell value={s.areas} label={s.areas === 1 ? 'area covered' : 'areas covered'} />
      </div>
      {wait.kind === 'ready' ? (
        <WorkspaceButton
          size="md"
          variant="primary"
          onClick={() => {
            showPacket(wait.file);
            packet.reset();
          }}
        >
          <Download className="h-3.5 w-3.5" aria-hidden="true" />
          Open packet
        </WorkspaceButton>
      ) : (
        <WorkspaceButton
          size="md"
          disabled={wait.kind === 'fetching' || wait.kind === 'preparing'}
          onClick={packet.start}
        >
          <Download className="h-3.5 w-3.5" aria-hidden="true" />
          {wait.kind === 'fetching'
            ? 'Opening the packet…'
            : wait.kind === 'preparing'
              ? 'Preparing the packet…'
              : 'Read the requirements packet (PDF)'}
        </WorkspaceButton>
      )}
      {wait.kind === 'preparing' ? (
        <div className="mt-2 flex items-center gap-2 text-meta text-ink-500">
          <Spinner /> The packet is being prepared. This usually takes under a minute.
        </div>
      ) : wait.kind === 'ready' ? (
        <div className="mt-2 text-meta text-ink-500">The packet is ready.</div>
      ) : wait.kind === 'failed' ? (
        <div role="alert" className="mt-2 text-meta text-posture-critical">
          {problemText(wait.error, 'The packet could not be opened.')}
        </div>
      ) : null}
      <div className="mt-2 text-meta text-ink-500">
        Each requirement in the packet shows why it is there: who said it, in which session, or
        which document it came from.
      </div>
      <hr className="my-5 border-ink-100" />

      {changes !== null ? (
        <div>
          <label htmlFor="cs-changes" className="mb-1.5 block text-meta font-medium text-ink-700">
            What should change?
          </label>
          <textarea
            id="cs-changes"
            rows={4}
            maxLength={MAX_COMMENT}
            value={changes}
            onChange={(e) => setChanges(e.target.value)}
            placeholder="Which requirements, and what is different, missing or wrong"
            className="block w-full resize-y rounded-card border border-ink-200 bg-paper-0 px-3 py-2 text-body leading-5 text-ink-900 placeholder:text-ink-300 hover:border-ink-300 focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-accent-amber-600"
          />
          <div className="mt-1.5 text-meta text-ink-500">
            {payload.requested_by ?? 'Avero Advisors'} sees it at once, and nothing is signed.
          </div>
          {problem ? (
            <div role="alert" className="mt-3 text-meta text-posture-critical">
              {problem}
            </div>
          ) : null}
          <div className="mt-5 flex justify-between gap-2">
            <WorkspaceButton size="md" variant="tertiary" onClick={() => setChanges(null)}>
              Back to signing
            </WorkspaceButton>
            <WorkspaceButton
              size="md"
              variant="primary"
              disabled={busy || !changes.trim()}
              onClick={() => void sendChanges()}
            >
              {busy ? 'Sending…' : 'Send my request'}
            </WorkspaceButton>
          </div>
        </div>
      ) : (
        <div>
          <label className="flex cursor-pointer items-start gap-2.5 text-body text-ink-900">
            <input
              type="checkbox"
              checked={attested}
              onChange={(e) => setAttested(e.target.checked)}
              className="peer sr-only"
            />
            <span
              aria-hidden="true"
              className={cn(
                'mt-[3px] inline-flex h-4 w-4 flex-none items-center justify-center rounded-[4px] border-[1.5px] peer-focus-visible:ring-2 peer-focus-visible:ring-accent-amber-600 peer-focus-visible:ring-offset-2',
                attested ? 'border-ink-900 bg-ink-900 text-paper-0' : 'border-ink-300 bg-paper-0',
              )}
            >
              {attested ? <Check className="h-3 w-3" /> : null}
            </span>
            <span>
              I have read the packet. These requirements reflect what {session.client_name} needs,
              as of today.
            </span>
          </label>
          <label
            htmlFor="cs-name"
            className="mb-1.5 mt-3.5 block text-meta font-medium text-ink-700"
          >
            Type your full name to sign
          </label>
          <WorkspaceInput
            id="cs-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={signer.full_name}
            autoComplete="off"
            aria-describedby="cs-name-help"
            className="max-w-[360px]"
          />
          <div
            id="cs-name-help"
            className={cn(
              'mt-1.5 text-meta',
              name.trim() && !nameOk ? 'text-posture-critical' : 'text-ink-500',
            )}
          >
            {name.trim() && !nameOk
              ? `Type your name as it appears above: ${signer.full_name}`
              : `Signing as ${nameAndTitle(signer.full_name, signer.title)}. This link was sent to ${signer.email}.`}
          </div>
          {problem ? (
            <div role="alert" className="mt-3 text-meta text-posture-critical">
              {problem}
            </div>
          ) : null}
          <div className="mt-5 flex justify-between gap-2">
            <WorkspaceButton
              size="md"
              variant="tertiary"
              onClick={() => {
                setProblem(null);
                setChanges('');
              }}
            >
              Request changes instead
            </WorkspaceButton>
            <WorkspaceButton
              size="md"
              variant="primary"
              disabled={busy || !attested || !nameOk}
              onClick={() => void sign()}
            >
              {busy ? 'Signing…' : 'Sign'}
            </WorkspaceButton>
          </div>
        </div>
      )}
    </ClientCard>
  );
}

type Answer = { kind: 'signed'; at: string | null } | { kind: 'changes' };

/** The answer the session already records, if any. */
function answerOf(payload: SignoffPayload): Answer | null {
  if (payload.state === 'signed') return { kind: 'signed', at: payload.signed?.at ?? null };
  if (payload.state === 'changes_requested') return { kind: 'changes' };
  return null;
}

function SumCell({ value, label }: { value: number; label: string }) {
  return (
    <div>
      <div className="text-[24px] font-semibold leading-[30px] tracking-[-0.01em] text-ink-900 tabular-nums">
        {value.toLocaleString()}
      </div>
      <div className="text-meta text-ink-500">{label}</div>
    </div>
  );
}
